import type { ImpersonationState } from './impersonation.model';
import { setOverrides, startImpersonation } from './impersonation.repository';
import { getImpersonationStateSnapshot } from './impersonation.store';

export const IMPERSONATION_LINK_PARAM = 'impersonation';

const LINK_VERSION = 1;

interface ImpersonationLinkPayload {
  v: number;
  state: ImpersonationState;
}

const toBase64Url = (input: string): string => {
  const bytes = new TextEncoder().encode(input);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const fromBase64Url = (input: string): string => {
  const binary = atob(input.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

export const encodeImpersonationLink = (state: ImpersonationState): string => {
  const payload: ImpersonationLinkPayload = { v: LINK_VERSION, state };
  return toBase64Url(JSON.stringify(payload));
};

const isImpersonationState = (value: unknown): value is ImpersonationState => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const state = value as Record<string, unknown>;
  return (
    typeof state['isImpersonating'] === 'boolean' &&
    (state['impersonatedUserId'] === null ||
      typeof state['impersonatedUserId'] === 'string') &&
    (state['impersonatedUser'] === null ||
      typeof state['impersonatedUser'] === 'object') &&
    (state['overrides'] === null || typeof state['overrides'] === 'object')
  );
};

/** Decodes a link payload. Returns null on any malformed/unknown input — never throws. */
export const decodeImpersonationLink = (
  encoded: string
): ImpersonationState | null => {
  try {
    const parsed: unknown = JSON.parse(fromBase64Url(encoded));
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }
    const payload = parsed as Record<string, unknown>;
    if (payload['v'] !== LINK_VERSION) {
      return null;
    }
    return isImpersonationState(payload['state']) ? payload['state'] : null;
  } catch {
    return null;
  }
};

/**
 * Current page URL with the impersonation param encoding the live store state.
 * Browser-only — call from event handlers, not at module load.
 */
export const buildImpersonationLink = (): string => {
  const url = new URL(window.location.href);
  url.searchParams.set(
    IMPERSONATION_LINK_PARAM,
    encodeImpersonationLink(getImpersonationStateSnapshot())
  );
  return url.toString();
};

export const applyImpersonationState = (state: ImpersonationState): void => {
  if (state.isImpersonating && state.impersonatedUser) {
    startImpersonation(state.impersonatedUser);
  } else if (state.overrides) {
    setOverrides(state.overrides);
  }
};
